import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Formal corporate KPI band for the Rent Roll header. Crisp slate accents,
 * tabular numerics, subtle top-border accent for hierarchy.
 */
export function IncomeKPICards({ data, year }) {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const maxMonth = year === now.getFullYear() ? currentMonth : 12;
  const monthsUpToCurrent = data.months.filter((m) => m.month <= maxMonth);
  const totalUpToCurrent = monthsUpToCurrent.reduce((sum, m) => sum + (m.total || 0), 0);
  const count = monthsUpToCurrent.length;
  const avg = count > 0 ? Math.round(totalUpToCurrent / count) : 0;

  // vs-previous-month delta on the current-month card
  let delta = 0;
  let prev = 0;
  if (year === now.getFullYear() && currentMonth > 1) {
    prev = data.months.find((m) => m.month === currentMonth - 1)?.total || 0;
    delta = prev > 0 ? ((data.current_month_total - prev) / prev) * 100 : 0;
  }
  const DeltaIcon = delta > 0.5 ? TrendingUp : delta < -0.5 ? TrendingDown : Minus;
  const deltaColor = delta > 0.5 ? 'text-emerald-700' : delta < -0.5 ? 'text-rose-700' : 'text-slate-500';

  const Card = ({ label, value, suffix, sub, accent }) => (
    <div className="relative rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className={`absolute inset-x-0 top-0 h-0.5 ${accent}`} />
      <div className="px-5 pt-5 pb-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-heading text-[28px] leading-none font-semibold tabular-nums text-slate-900">${fmt(value)}</span>
          {suffix}
        </div>
        {sub && <p className="mt-2 text-[11px] text-slate-500 tabular-nums">{sub}</p>}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="income-kpi-cards">
      <Card
        label={`Current Month · ${MONTH_NAMES[currentMonth]} ${now.getFullYear()}`}
        value={data.current_month_total}
        accent="bg-slate-900"
        suffix={year === now.getFullYear() && currentMonth > 1 ? (
          <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${deltaColor}`}>
            <DeltaIcon className="h-3 w-3" /> {Math.abs(delta).toFixed(1)}%
          </span>
        ) : null}
        sub={year === now.getFullYear() && currentMonth > 1 ? `vs ${MONTH_NAMES[currentMonth - 1]} · $${fmt(prev)}` : null}
      />
      <Card
        label={`FY ${year} — Total Rent Collected`}
        value={data.yearly_total}
        accent="bg-emerald-700"
        sub={`${data.months.filter((m) => m.total > 0).length} of 12 months with activity`}
      />
      <Card
        label="Monthly Average (YTD)"
        value={avg}
        accent="bg-slate-400"
        sub={`$${fmt(totalUpToCurrent)} over ${count} month${count === 1 ? '' : 's'}`}
      />
    </div>
  );
}
